import { PayloadAction, ReducerCreators } from '@reduxjs/toolkit';

import { TemplatesState } from './types';
import {
  AddUserTemplateFavorite,
  DeleteUserTemplateFavorite,
  GetUserTemplateFavorites,
  TUpdateTemplateFavoriteParams,
} from '@/connection/templates';
import { CreateNewColorPack, GetThemePacks, SaveColor } from '@/connection/theme-packs';
import { RootState } from '@/store/main';
import { CreateThemePack, FontPack, ThemePack, UpdateThemePack } from '@/types/theme-packs.types';
import { GetProjects } from '@/connection/projects/get-projects';
import { EventsTotals, GetEventsResponse } from '@/types/projects.types';
import toastTrigger from '@ui/toast/toast-trigger';
import GetTemplateFilters from '@/connection/projects/get-template-filters';
import { IEventTemplateFilters } from '@/types/projects.types';
import { PAGINATION_LIMIT } from '@/utils/pagination-constants';
import abortable from '@/connection/abortable';
import { isObject } from 'underscore';
import { GetFonts } from '@/connection/font-packs';

export const reducers = (create: ReducerCreators<TemplatesState>) => ({
  fetchThemePacks: create.asyncThunk(
    async (
      { channel, returnStoreStateIfExists = true }: { channel: number; returnStoreStateIfExists?: boolean },
      { getState }
    ) => {
      if (returnStoreStateIfExists) {
        // return from state if exists
        const state = getState() as RootState;
        const themePacks = state?.templates?.themePacks as ThemePack[] | null;
        if (themePacks) {
          return themePacks;
        }
      }
      return GetThemePacks(channel);
    },
    {
      fulfilled: (state, action) => {
        state.themePacks = action.payload;
      },
      pending: (state) => {
        state.loadingThemePacks = true;
      },
      rejected: (_, action) => {
        console.error(action.error);
        // TODO
        // add an error to state and show alert in the UI component
      },
      settled: (state) => {
        state.loadingThemePacks = false;
      },
    }
  ),
  saveThemePack: create.asyncThunk(
    async (themePack: UpdateThemePack) => {
      return SaveColor(themePack);
    },
    {
      fulfilled: (state, action) => {
        state.themePacks = state.themePacks?.map((theme) => {
          if (theme.color_pack === action.payload.color_pack) {
            return action.payload;
          }
          return theme;
        }) || [action.payload];
      },
      pending: (state, action) => {
        state.savingThemePack = action.meta?.arg?.color_pack || null;
      },
      rejected: (_, action) => {
        console.error(action.error);
      },
      settled: (state) => {
        state.savingThemePack = null;
      },
    }
  ),
  createThemePack: create.asyncThunk(
    async (themePack: CreateThemePack) => {
      return CreateNewColorPack(themePack);
    },
    {
      fulfilled: (state, action) => {
        if (state.themePacks) {
          state.themePacks.push(action.payload);
        } else {
          state.themePacks = [action.payload];
        }
      },
      pending: (state) => {
        state.creatingThemPack = true;
      },
      rejected: (_, action) => {
        console.error(action.error);
      },
      settled: (state) => {
        state.creatingThemPack = false;
      },
    }
  ),
  fetchThemesAndTemplates: create.asyncThunk(
    async (
      {
        channel,
        returnStoreStateIfExists = true,
        offset,
        limit,
        search,
        includeTemplatesChannel,
        noV1Events,
        templateFilters,
        groupUuid,
        append,
        resetToPageNumber,
      }: {
        channel: number;
        returnStoreStateIfExists?: boolean;
        offset?: number;
        limit?: number;
        includeTemplatesChannel?: boolean;
        search?: string;
        noV1Events?: boolean;
        templateFilters?: { design?: string; categories?: string; favorites?: string; recent?: string };
        groupUuid?: string;
        append?: boolean;
        resetToPageNumber?: number;
      },
      { getState, rejectWithValue }
    ) => {
      try {
        if (returnStoreStateIfExists) {
          const state = getState() as RootState;
          // return from state if exists
          const themePacks = state?.templates?.themePacks as ThemePack[] | null;
          const templates = state?.templates?.templates as GetEventsResponse[] | null;
          const totalTemplates = state?.templates?.totalTemplates as EventsTotals | null;

          if (themePacks && templates) {
            return { templates, themePacks, totalTemplates, append, resetToPageNumber };
          }
        }

        abortable.abort('GetProjects'); // abort any previous calls
        // abortable.abort('GetThemePacks'); // abort any previous calls

        const results: [Awaited<ReturnType<typeof GetProjects>>, Awaited<ReturnType<typeof GetThemePacks>>] =
          await Promise.all([
            GetProjects({
              channel,
              limit: limit || PAGINATION_LIMIT,
              offset: offset || 0,
              search,
              includeTemplatesChannel,
              noV1Events,
              templateFilters,
              selectedEventGroupUuid: groupUuid,
            }),
            GetThemePacks(channel),
          ]);
        const templates = results[0].events || [];
        const totalTemplates = results[0].totals || 0;
        const themePacks = results[1];
        return { templates, totalTemplates, themePacks, append, resetToPageNumber };
      } catch (e) {
        if (isObject(e) && e.body === 'Navigation') {
          // this just means we aborted, we will use this in the rejected handler to not throw a user message
          return rejectWithValue('Navigation');
        }
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.themePacks = action.payload.themePacks;
          if (action.payload.append) {
            state.templates = [...(state.templates || []), ...(action.payload.templates || [])];
          } else {
            state.templates = action.payload.templates;
            state.templatesListPage = 1;
          }
          state.totalTemplates = action.payload.totalTemplates;

          if (action.payload.resetToPageNumber !== undefined) {
            state.templatesListPage = action.payload.resetToPageNumber;
          }
        }
        state.loadingTemplatesAndThemes = false;
        state.renderLoadingList = 0;
      },
      pending: (state) => {
        state.loadingTemplatesAndThemes = true;
      },
      rejected: (state, action) => {
        if (action.payload instanceof Error) {
          console.error(action.payload);
        } else {
          console.error(action.error);
        }
        state.loadingTemplatesAndThemes = false;
        state.renderLoadingList = 0;

        // don't throw a user message if we are simply abort the request
        if (action.payload === 'Navigation') {
          return;
        }
        toastTrigger('There was an error loading templates and themes. Please refresh the page.', {
          type: 'error',
          autoClose: 10000,
          toastId: 'templates-loading-error',
        });
      },
    }
  ),
  fetchTemplateFilters: create.asyncThunk(
    async ({ channel }: { channel: number }, { getState, rejectWithValue }) => {
      try {
        const state = getState() as RootState;
        const templateFilters = state?.templates?.templateFilters as IEventTemplateFilters | null;
        if (templateFilters) {
          return { templateFilters };
        }
        const results = await GetTemplateFilters({ channel });

        return results;
      } catch (error) {
        console.error(error);
        throw rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload?.templateFilters) {
          state.templateFilters = action.payload.templateFilters;
        }
        state.loadingTemplateFilters = false;
      },
      pending: (state) => {
        state.loadingTemplateFilters = true;
      },
      rejected: (state, action) => {
        console.error(action.error);
        state.loadingTemplateFilters = false;
      },
    }
  ),
  fetchUserTemplateFavorites: create.asyncThunk(
    async ({ channel }: { channel: number }, { rejectWithValue }) => {
      try {
        const results = await GetUserTemplateFavorites({ channel });
        return results;
      } catch (error) {
        console.error(error);
        throw rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.userFavorites = action.payload;
        }
        state.loadingUserFavorites = false;
      },
      pending: (state) => {
        state.loadingUserFavorites = true;
      },
      rejected: (state) => {
        state.loadingUserFavorites = false;
      },
    }
  ),
  deleteUserTemplateFavorite: create.asyncThunk(
    async ({ activeChannel, adminId, projectUuid }: TUpdateTemplateFavoriteParams, { rejectWithValue }) => {
      try {
        await DeleteUserTemplateFavorite({ activeChannel, adminId, projectUuid });
        return projectUuid;
      } catch (error) {
        console.error(error);
        throw rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.userFavorites) {
          state.userFavorites = state.userFavorites?.filter((f) => f.event_uuid !== action.payload);
        }
      },
      rejected: (_, action) => {
        console.error(action.error);
        toastTrigger('Failed to update favorites. Please refresh and try again.', {
          type: 'error',
        });
      },
    }
  ),
  addUserTemplateFavorite: create.asyncThunk(
    async ({ activeChannel, adminId, projectUuid }: TUpdateTemplateFavoriteParams, { rejectWithValue }) => {
      try {
        return AddUserTemplateFavorite({ activeChannel, adminId, projectUuid });
      } catch (error) {
        console.error(error);
        throw rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.userFavorites = state.userFavorites || [];
          state.userFavorites.push(action.payload);
        }
      },
      rejected: (_, action) => {
        console.error(action.error);
        toastTrigger('Failed to update favorites. Please refresh and try again.', {
          type: 'error',
        });
      },
    }
  ),
  deleteThemePackSync: create.reducer((state, action: PayloadAction<number[]>) => {
    if (action.payload) {
      state.themePacks = state.themePacks?.filter((theme) => !action.payload.includes(theme.color_pack)) ?? [];
    }
  }),
  updateRefetchTemplatesCounter: create.reducer((state) => {
    state.refetchTemplatesCounter++;
  }),
  clearTemplatesList: create.reducer((state) => {
    state.templates = [];
    state.totalTemplates = null;
    state.templatesListPage = 1;
    state.templateFilters = null;
  }),
  setTemplateFiltersInStoreOnly: create.reducer((state, action: PayloadAction<IEventTemplateFilters>) => {
    state.templateFilters = action.payload;
  }),
  appendNewTheme: create.reducer((state, action: PayloadAction<{ theme: ThemePack; after?: number }>) => {
    if (state.themePacks) {
      if (!action.payload.after) {
        state.themePacks.push(action.payload.theme);
      } else {
        for (let i = 0; i < state.themePacks.length; ++i) {
          const theme = state.themePacks[i];
          if (theme.color_pack === action.payload.after) {
            state.themePacks?.splice(i + 1, 0, action.payload.theme);
            break;
          }
        }
      }
    } else {
      state.themePacks = [action.payload.theme];
    }
  }),
  updateTheme: create.reducer((state, action: PayloadAction<ThemePack>) => {
    state.themePacks = state.themePacks?.map((theme) => {
      if (theme.color_pack === action.payload.color_pack) {
        return action.payload;
      }
      return theme;
    }) || [action.payload];
  }),
  fetchFontPacks: create.asyncThunk(
    async ({ channel }: { channel: number }) => {
      return GetFonts(channel);
    },
    {
      fulfilled: (state, action) => {
        state.fontPacks = action.payload;
      },
      pending: (state) => {
        state.loadingFontPacks = true;
      },
      rejected: (_, action) => {
        console.error(action.error);
      },
      settled: (state) => {
        state.loadingFontPacks = false;
      },
    }
  ),
  updateFontPacks: create.reducer((state, action: PayloadAction<FontPack>) => {
    const existingFontPack = state.fontPacks?.find((pack) => pack.font_pack === action.payload.font_pack);

    //if a match was found when creating a font pack, do not re add to the store
    if (existingFontPack) return;

    state.fontPacks = [...(state.fontPacks || []), action.payload];
  }),
});
